import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, InputAdornment, Stack, Tabs } from '@mui/material';
import { CalculsFormNewPerteGainsProfessionnelsFuturs } from '@norma/shared/constants/calculs';
import { newPGPFDefaultValues } from '@norma/shared/constants/prejudice/defaultValues/FormPerteGainsProfessionnels.defaultValues';
import {
  getCapitalisationBaremes,
  getShouldNotDisplayCapitalisation,
} from '@norma/shared/helpers/prejudices/capitalisation';
import {
  NewPrejudiceFormPerteGainProfessionnelsFuturs,
  PertesDeGainsProfessionnelsPGPF,
  PrejudiceType,
} from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import i18next from 'i18next';
import { intersection, omit } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DintilhacText } from 'src/components/client/prejudiceFormComponents/DintilhacText';
import { ComputedReadFieldForm } from 'src/components/forms/ComputedReadFieldForm';
import {
  getDefaultMinAndMaxDate,
  validationSchemaPrejudiceBase,
  validationSchemaPrejudiceFormCapitalisationOrRenteBase,
  validationSchemaTiersPayeursCapitalisationObject,
} from 'src/constants/prejudice/validation';
import { dateString } from 'src/helpers/yup';
import * as yup from 'yup';
import { CalculsBox } from '../../basic/CalculsBox';
import { ExpandableTextFieldForm } from '../../forms/ExpandableTextFieldForm';
import { PrejudiceContainer } from '../PrejudiceContainer';
import { PrejudiceFormProps } from '../PrejudiceFormProps';
import { PrejudiceTab } from '../PrejudiceTab';
import { SavePrejudiceButton } from '../SavePrejudiceButton';
import { TotalCapitalisation } from '../prejudiceFormComponents/Capitalisation/TotalCapitalisation';
import { DintilhacButton } from '../prejudiceFormComponents/DintilhacButton';
import { NewStepManager } from '../prejudiceFormComponents/perteGainsProfessionnelsActuel/NewStepManager';
import { TotalNewPerteGainsFuturs } from '../prejudiceFormComponents/perteGainsProfessionnelsActuel/TotalNewPerteGainsFuturs';
import {
  validationSchemaNewFormPerteGainsProfessionnelsActuel,
  validationSchemaNewFormPerteGainsProfessionnelsActuelIndmeniteJournalieresPercuesPendantLaPeriodeDArret,
  validationSchemaNewFormPerteGainsProfessionnelsActuelIndmeniteJournalieresPercuesPendantLaPeriodeDArretRow,
  validationSchemaNewFormPerteGainsProfessionnelsActuelPerteDeGainsProfessionnels,
  validationSchemaNewFormPerteGainsProfessionnelsActuelPerteDeGainsProfessionnelsRow,
  validationSchemaNewFormPerteGainsProfessionnelsActuelRevenuActiviteAnnuelDeReference,
  validationSchemaNewFormPerteGainsProfessionnelsActuelRevenuActiviteAnnuelDeReferenceRow,
} from './NewFormPerteGainsProfessionnelsActuel';

export const validationSchemaNewFormPerteGainsProfessionnelsFutursPerteDeGainsProfessionnels =
  ({
    victime,
    procedure,
    prejudiceType,
  }: {
    victime: Victime;
    procedure: Procedure;
    prejudiceType: PrejudiceType;
  }): yup.ObjectSchema<PertesDeGainsProfessionnelsPGPF> =>
    validationSchemaNewFormPerteGainsProfessionnelsActuelPerteDeGainsProfessionnels(
      {
        victime,
        procedure,
        prejudiceType,
        isPGPF: true,
      },
    ).shape({
      annualisation: yup.number().required().min(0),
    });

export const validationSchemaNewFormPerteGainsProfessionnelsFuturs = ({
  procedure,
  victime,
  prejudiceType,
}: {
  procedure: Procedure;
  victime: Victime;
  prejudiceType: PrejudiceType;
}): yup.ObjectSchema<NewPrejudiceFormPerteGainProfessionnelsFuturs> => {
  const { minDate, maxDate } = getDefaultMinAndMaxDate({
    procedure,
    victime,
    prejudiceType,
  });
  return validationSchemaPrejudiceBase
    .concat(validationSchemaPrejudiceFormCapitalisationOrRenteBase)
    .concat(
      validationSchemaNewFormPerteGainsProfessionnelsActuel({
        victime,
        procedure,
        prejudiceType,
      }),
    )
    .shape({
      revenuActiviteAnnuelDeReference:
        validationSchemaNewFormPerteGainsProfessionnelsActuelRevenuActiviteAnnuelDeReference(
          {
            victime,
            procedure,
          },
        ).shape({
          rows: yup
            .array()
            .defined()
            .of(
              validationSchemaNewFormPerteGainsProfessionnelsActuelRevenuActiviteAnnuelDeReferenceRow(
                {
                  victime,
                  procedure,
                  isPGPF: true,
                },
              ),
            ),
        }),
      perteDeGainsProfessionnels:
        validationSchemaNewFormPerteGainsProfessionnelsFutursPerteDeGainsProfessionnels(
          {
            victime,
            procedure,
            prejudiceType,
          },
        ).shape({
          rows: yup
            .array()
            .defined()
            .of(
              validationSchemaNewFormPerteGainsProfessionnelsActuelPerteDeGainsProfessionnelsRow(
                {
                  victime,
                  procedure,
                  prejudiceType,
                  isPGPF: true,
                },
              ).shape({
                dateDebut: yup
                  .string()
                  .isDateString()
                  .optional()
                  .nullable()
                  .defined()
                  .minDate(undefined, minDate.message, minDate.date)
                  .maxDate(undefined, maxDate.message, maxDate.date),
              }),
            ),
        }),
      indemnitesJournalieresPercuesPendantLaPeriodeDArret:
        validationSchemaNewFormPerteGainsProfessionnelsActuelIndmeniteJournalieresPercuesPendantLaPeriodeDArret(
          {
            victime,
            procedure,
            prejudiceType,
          },
        ).shape({
          rows: yup
            .array()
            .defined()
            .of(
              validationSchemaNewFormPerteGainsProfessionnelsActuelIndmeniteJournalieresPercuesPendantLaPeriodeDArretRow(
                {
                  victime,
                  procedure,
                  prejudiceType,
                },
              ).shape({
                dateDebut: dateString()
                  .optional()
                  .nullable()
                  .defined()
                  .minDate(undefined, minDate.message, minDate.date)
                  .maxDate(undefined, maxDate.message, maxDate.date),
              }),
            ),
        }),
      typeBareme: yup.string().optional().nullable(),
      ageDernierArrerage: yup.lazy((value) =>
        value === ''
          ? (yup
              .string()
              .defined()
              .transform(() => null) as unknown as yup.NumberSchema<
              number,
              yup.AnyObject,
              undefined,
              ''
            >)
          : yup.number().defined().nullable().min(0),
      ),
      isLastArrerageViager: yup.boolean().optional(),
      baremeCapitalisation: yup.string().optional(),
      victimeSommeACapitaliser: yup.number().required().min(0),
      prixEuroRente: yup.number().nullable().defined().min(0),
      renteCapitalisee: yup.number().required().min(0),
      capitalisationTiersPayeurs: yup.object(
        omit(validationSchemaTiersPayeursCapitalisationObject(), [
          'montantCapitalise',
        ]),
      ),
      tiersPayeursTotalCapitalise: yup.number().required().min(0),
      ageDateAttribution: yup.number().optional(),
      capitalConstitutifRente: yup.number().required(),

      commentaires: yup.string().optional().defined(),
      PGPFReliquat: yup.number().nullable().defined(),
      activatePGPFReliquat: yup.boolean().nullable().defined(),
      total: yup.number().required(),
      formType: yup.string().oneOf(['REVENUS']).required(),
      revalorisationCoefficientsType: yup
        .string()
        .oneOf(['annuel', 'smic'])
        .required(),
      enableCapitalisationDifferee: yup.boolean().optional(),
    });
};

interface Props
  extends PrejudiceFormProps<NewPrejudiceFormPerteGainProfessionnelsFuturs> {
  tauxIPP: number | null;
}

export const NewFormPerteGainsProfessionnelsFuturs: React.FC<Props> = ({
  victime,
  procedure,
  dommages,
  values,
  baremes,
  dateLiquidation,
  monetaryErosions,
  onPrevious,
  dateConsolidation,
  tauxIPP,
  allNumerosPieces,
  dateDeces,
  ...props
}) => {
  const [activeTab, setActiveTabs] = useState(0);
  const { t } = useTranslation();

  const capitalisationBaremes = useMemo(
    () => getCapitalisationBaremes(baremes, victime.sexe),
    [baremes, victime.sexe],
  );
  const useFormReturn = useForm<NewPrejudiceFormPerteGainProfessionnelsFuturs>({
    defaultValues: newPGPFDefaultValues({
      procedure,
      values,
    }),
    resolver: yupResolver(
      validationSchemaNewFormPerteGainsProfessionnelsFuturs({
        procedure,
        victime,
        prejudiceType: props.prejudiceType,
      }),
    ),
  });
  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useFormReturn;

  const isFirstTabError = useMemo(() => {
    const firstTabFields: (keyof NewPrejudiceFormPerteGainProfessionnelsFuturs)[] =
      [
        'revenuActiviteAnnuelDeReference',
        'perteDeGainsProfessionnels',
        'indemnitesJournalieresPercuesPendantLaPeriodeDArret',
        'perteDeChanceDeGainProfessionnel',
      ];
    return (
      intersection(Object.keys(errors), firstTabFields).length > 0 &&
      activeTab !== 0
    );
  }, [errors, activeTab]);

  const isSecondTabError = useMemo(() => {
    const secondTabFields: (keyof NewPrejudiceFormPerteGainProfessionnelsFuturs)[] =
      [
        'typeBareme',
        'ageDernierArrerage',
        'isLastArrerageViager',
        'baremeCapitalisation',
        'victimeSommeACapitaliser',
        'prixEuroRente',
        'renteCapitalisee',
        'capitalisationTiersPayeurs',
        'capitalConstitutifRente',
        'activatePGPFReliquat',
        'PGPFReliquat',
      ];
    return (
      intersection(Object.keys(errors), secondTabFields).length > 0 &&
      activeTab !== 1
    );
  }, [errors, activeTab]);
  const shouldNotDisplayCapitalisation = getShouldNotDisplayCapitalisation({
    dateConsolidation,
    dateLiquidation,
    dateDeces,
  });

  const [showDintilhac, setShowDintilhac] = React.useState<boolean>(false);

  return (
    <>
      <PrejudiceContainer<NewPrejudiceFormPerteGainProfessionnelsFuturs>
        {...props}
        victime={victime}
        procedure={procedure}
        monetaryErosions={monetaryErosions}
        baremes={baremes}
        {...useFormReturn}
        renderPrejudice={({ setAdditionalTitle }) => (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <ExpandableTextFieldForm
                control={control}
                name="notes"
                label={t('prejudice.fields.notes.label')}
                placeholder={t('prejudice.fields.notes.placeholder') || ''}
                dialogTitle={t('prejudice.fields.notes.label')}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <DintilhacButton onClick={() => setShowDintilhac(true)} />
                    </InputAdornment>
                  ),
                }}
                multiline
                maxRows={4}
                fullWidth
              />
              <DintilhacText
                open={showDintilhac}
                setOpen={setShowDintilhac}
                label={t('prejudice.fields.dintilhac.label')}
                content={i18next.t(
                  `prejudice.prejudicesTypes.${props.prejudiceType}.introduction`,
                  {
                    defaultValue: '',
                  },
                )}
              />
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTabs(value)}
              >
                <PrejudiceTab
                  label={t(
                    'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_FUTURS.tab.PGPF',
                    {
                      context: victime.dateDeces ? 'deces' : undefined,
                    },
                  )}
                  value={0}
                  isError={isFirstTabError}
                />
                {!shouldNotDisplayCapitalisation ? (
                  <PrejudiceTab
                    label={t(
                      'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_FUTURS.tab.capitalisation',
                      {
                        context: victime.dateDeces ? 'deces' : undefined,
                      },
                    )}
                    value={1}
                    isError={isSecondTabError}
                  />
                ) : null}
                <PrejudiceTab
                  label={
                    t(
                      'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_FUTURS.tab.total',
                    ) || ''
                  }
                  value={2}
                />
              </Tabs>
            </Box>
            <Stack
              direction="column"
              spacing={4}
              sx={{ display: activeTab === 0 ? '' : 'none' }}
            >
              <NewStepManager
                setAdditionalTitle={setAdditionalTitle}
                procedure={procedure}
                dommage={dommages?.[0]}
                prejudiceType={props.prejudiceType}
                victime={victime}
                control={control}
                trigger={trigger}
                values={values}
                getValues={getValues}
                onClose={props.onClose}
                monetaryErosions={monetaryErosions}
                onPrevious={onPrevious ? onPrevious : undefined}
                handleSubmit={handleSubmit}
                isPGPF
                dateConsolidation={dateConsolidation}
                dateLiquidation={dateLiquidation}
                tauxIPP={tauxIPP}
                allNumerosPieces={allNumerosPieces}
                user={props.user}
              />
            </Stack>
            {!shouldNotDisplayCapitalisation ? (
              <Stack
                direction="column"
                sx={{ display: activeTab === 1 ? '' : 'none' }}
                spacing={2}
              >
                <Grid container spacing={2}>
                  <Grid item md={6}>
                    <TotalCapitalisation
                      control={control}
                      victimeName={{
                        sommeACapitaliser: 'victimeSommeACapitaliser',
                        bareme: 'baremeCapitalisation',
                        coefficient: 'prixEuroRente',
                        montantCapitalise: 'renteCapitalisee',
                        isLastArrerageViager: 'isLastArrerageViager',
                        ageDernierArrerage: 'ageDernierArrerage',
                        ageDateAttribution: 'ageDateAttribution',
                        enableCapitalisationDifferee:
                          'enableCapitalisationDifferee',
                      }}
                      procedure={procedure}
                      dateLiquidation={
                        dateLiquidation ? new Date(dateLiquidation) : null
                      }
                      baremes={capitalisationBaremes}
                      dateNaissance={
                        victime.dateNaissance
                          ? new Date(victime.dateNaissance)
                          : null
                      }
                      dateDeces={
                        victime.dateDeces ? new Date(victime.dateDeces) : null
                      }
                      sexe={victime.sexe}
                      tiersPayeursName={{
                        root: 'capitalisationTiersPayeurs.parTiersPayeur',
                        montantCapitalise: 'tiersPayeursTotalCapitalise',
                      }}
                      tiersPayeurSuffix={{
                        sommeACapitaliser: 'sommeACapitaliser',
                        montantCapitalise: 'montantCapitalise',
                        coefficient: 'coefficient',
                      }}
                      titles={{
                        total: {
                          title:
                            t(
                              'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_FUTURS.capitalisation.fields.total.title',
                            ) || '',
                        },
                        victime: {
                          sommeACapitaliser: t(
                            'prejudice.prejudicesFormTypes.PERTE_GAINS_PROFESSIONNELS_FUTURS.capitalisation.fields.victime.sommeACapitaliser.title',
                          ),
                        },
                      }}
                      defaultValuesNames={{
                        victimeSommeACapitaliser:
                          'perteDeGainsProfessionnels.annualisation',
                      }}
                      totalName="capitalConstitutifRente"
                      editable={{
                        victime: {
                          montantCapitalise: true,
                          ageDateAttribution: true,
                        },
                      }}
                      editedFieldsName="editedFields"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <CalculsBox>
                      <Stack width="98%">
                        <ExpandableTextFieldForm
                          control={control}
                          name="commentaires"
                          label="commentaires"
                          dialogTitle="Commentaires"
                          multiline
                          inputProps={{
                            sx: { minHeight: '200px' },
                          }}
                          maxRows={9}
                          fullWidth
                        />
                      </Stack>
                    </CalculsBox>
                  </Grid>
                </Grid>
                <SavePrejudiceButton
                  sx={{ alignSelf: 'center' }}
                  victime={victime}
                />
              </Stack>
            ) : null}
            <Stack sx={{ display: activeTab === 2 ? '' : 'none' }} spacing={4}>
              <TotalNewPerteGainsFuturs
                control={control as any}
                procedure={procedure}
                dateConsolidation={dateConsolidation}
                dateLiquidation={dateLiquidation}
                dateDeces={dateDeces}
              />
              <SavePrejudiceButton
                sx={{ alignSelf: 'center' }}
                victime={victime}
              />
            </Stack>
            <ComputedReadFieldForm
              control={control}
              name="perteDeGainsProfessionnels.annualisation"
              watchFields={['perteDeGainsProfessionnels.rows', 'editedFields']}
              compute={([rows, editedFields]) =>
                CalculsFormNewPerteGainsProfessionnelsFuturs.getPerteDeGainAnnualisee(
                  {
                    rows,
                    editedFields,
                  },
                ) || 0
              }
            />
          </>
        )}
      />
    </>
  );
};
